import type { SupportedLanguage } from "@amtana/i18n";
import { withProductName } from "@amtana/i18n/catalog";

import { LEGAL_FILLS } from "./legal-entity.ts";
import { LEGAL_EN } from "./legal-words-en.ts";
import { LEGAL_ES } from "./legal-words-es.ts";
import type { LegalWords } from "./legal-words.ts";

// The legal pages in every language (ADR-024): each text with `{product}` read as the product's
// name and `{company}`, `{address}` … as the company's facts, written once in legal-entity.ts.

/** Where each legal page lives; the footer, sign-up and the screens read these. */
export const LEGAL_PATHS = {
  terms: "/legal/terms",
  privacy: "/legal/privacy",
  notice: "/legal/notice",
} as const;

export type LegalPage = keyof typeof LEGAL_PATHS;

/** A text with the product's name and the company's facts in their places. */
function filled(text: string): string {
  return withProductName(text).replace(/\{(\w+)\}/g, (place, name: string) =>
    name in LEGAL_FILLS ? LEGAL_FILLS[name as keyof typeof LEGAL_FILLS] : place,
  );
}

/** Every string in `words`, at any depth, filled. */
function fillAll<T>(words: T): T {
  if (typeof words === "string") return filled(words) as T;
  if (Array.isArray(words)) return words.map((word: unknown) => fillAll(word)) as T;
  if (words !== null && typeof words === "object") {
    return Object.fromEntries(Object.entries(words).map(([key, value]) => [key, fillAll(value)])) as T;
  }
  return words;
}

export const LEGAL_CONTENT: Readonly<Record<SupportedLanguage, LegalWords>> = {
  en: fillAll(LEGAL_EN),
  es: fillAll(LEGAL_ES),
};

/** "Last updated 26 October 2026", with the date already in the reader's format. */
export function updatedLine(words: LegalWords, date: string): string {
  return words.chrome.updated.replace("{date}", date);
}
